"use client";
import type { CSSProperties } from "react";
import { useState } from "react";
import { DialogTrigger, Dialog as RACDialog } from "react-aria-components";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import { useColorMode } from "../../../utils/ColorModeProvider";
import { Checkbox } from "../../Checkbox";
import { Popover } from "../../Popover";
import { Tray } from "../../Tray";
// Map style preview images (base64 encoded for reliable cross-project usage)
import { neutralDark, neutralLight, sateliteLight, streetsDark, streetsLight } from "../assets/images";
import type { LayerSpec } from "../layers";
import type { MapType } from "../mapConfig";
import { ControlBase } from "./primitives/ControlBase";
import { ControlButton } from "./primitives/ControlButton";
import type { ControlPosition } from "./primitives/utils";

interface MapLayersControlProps {
  position?: ControlPosition;
  className?: string;
  /**
   * Custom positioning - overrides position prop
   * Example: { top: '20px', right: '20px' }
   */
  style?: CSSProperties;
  /**
   * Current map type
   */
  currentMapType?: MapType;
  /**
   * Callback when map type is changed
   */
  onMapTypeChange?: (mapType: MapType) => void;
  /**
   * Layers with legend definitions that should appear in the control
   * Only layers with a `legend` property will be shown
   */
  layers?: LayerSpec[];
  /**
   * Callback when a layer's visibility is toggled
   */
  onLayerToggle?: (layerId: string) => void;
  /**
   * Callback to clear all layer selections (hide all layers)
   */
  onClearAll?: () => void;
}

const getMapTypeOptions = (isDarkMode: boolean) => [
  {
    id: "streets" as const,
    name: "Streets",
    description: "Standard street map with traffic data",
    preview: isDarkMode ? streetsDark : streetsLight,
  },
  {
    id: "satellite" as const,
    name: "Satellite",
    description: "Aerial imagery with street labels",
    preview: sateliteLight, // Only light version available
  },
  {
    id: "neutral" as const,
    name: "Neutral",
    description: "Clean design with light/dark variants",
    preview: isDarkMode ? neutralDark : neutralLight,
  },
];

/**
 * Small visual swatch component for layer legends
 */
function Swatch({ type, color }: { type: "line" | "dot" | "polygon" | "pin"; color?: string }) {
  const baseClasses = "inline-block";
  const colorStyle = color ? { backgroundColor: color } : {};

  switch (type) {
    case "dot":
      return <span className={`${baseClasses} h-3 w-3 rounded-full border border-border`} style={colorStyle} />;
    case "line":
      return <span className={`${baseClasses} h-0.5 w-6 rounded-full`} style={colorStyle} />;
    case "polygon":
      return <span className={`${baseClasses} h-3 w-4 rounded-sm border border-border`} style={colorStyle} />;
    case "pin":
      return (
        <span
          className={`${baseClasses} h-3 w-3 rounded-full rounded-bl-none border border-border transform rotate-45`}
          style={colorStyle}
        />
      );
    default:
      return null;
  }
}

export function LayersControl({
  position = "bottom-left",
  className,
  style,
  currentMapType = "streets",
  onMapTypeChange,
  layers = [],
  onLayerToggle,
  onClearAll,
}: MapLayersControlProps) {
  const { isDarkTheme } = useColorMode();
  const { isMobile } = useBreakpoint();
  const [isTrayOpen, setIsTrayOpen] = useState(false);
  const mapTypeOptions = getMapTypeOptions(isDarkTheme);

  const handleMapTypeSelect = (mapType: MapType) => {
    onMapTypeChange?.(mapType);
  };

  // Filter layers that have legend definitions
  const legendLayers = layers.filter((layer) => layer.legend);

  // Content that will be rendered in both Tray and Popover
  const layersContent = (
    <div className={isMobile ? "space-y-6 pb-6" : "space-y-6"}>
      {/* Map Type Section */}
      <div>
        <div className="text-sm font-semibold text-text-body mb-3">Map type</div>
        <div className="grid grid-cols-3 gap-3">
          {mapTypeOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => handleMapTypeSelect(option.id)}
              className={
                "flex flex-col items-center space-y-2 p-3 rounded-lg text-center " +
                "hover:bg-background-muted transition-colors border border-transparent " +
                (currentMapType === option.id ? "bg-background-muted border-border" : "hover:border-border")
              }
            >
              <div
                className={
                  "w-12 h-12 rounded-lg overflow-hidden bg-background-surface " +
                  (currentMapType === option.id ? "border-2 border-action-default" : "border border-border-default")
                }
              >
                <img src={option.preview} alt={`${option.name} map preview`} className="w-full h-full object-cover" />
              </div>
              <div
                className={
                  "text-sm " +
                  (currentMapType === option.id ? "font-bold text-text-body" : "font-normal text-text-muted")
                }
              >
                {option.name}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Dynamic Layers Section */}
      {legendLayers.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-text-body">Map details</div>
            {onClearAll && (
              <button
                type="button"
                onClick={onClearAll}
                className="text-xs text-text-muted hover:text-text-body transition-colors"
              >
                Clear all
              </button>
            )}
          </div>
          <div className="space-y-2">
            {legendLayers.map((layer) => (
              <Checkbox key={layer.id} isSelected={layer.visible !== false} onChange={() => onLayerToggle?.(layer.id)}>
                <div className="flex items-center gap-2">
                  {layer.legend && <Swatch type={layer.legend.swatch} color={layer.legend.color} />}
                  <span className="text-sm text-text-body">{layer.legend?.label}</span>
                </div>
              </Checkbox>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <ControlBase position={position} className={className} style={style}>
      <div className="relative">
        {isMobile ? (
          <>
            <ControlButton icon="Stack" ariaLabel="Map layers and settings" onPress={() => setIsTrayOpen(true)} />
            <Tray isOpen={isTrayOpen} onClose={() => setIsTrayOpen(false)} title="Map layers" animationVariant="scale">
              {layersContent}
            </Tray>
          </>
        ) : (
          <DialogTrigger>
            <ControlButton icon="Stack" ariaLabel="Map layers and settings" />
            <Popover placement="top start">
              <RACDialog
                aria-label="Map layers and settings"
                className="p-4 min-w-80 bg-background-surface border border-border rounded-lg shadow-xl focus:outline-none focus:ring-0"
              >
                {layersContent}
              </RACDialog>
            </Popover>
          </DialogTrigger>
        )}
      </div>
    </ControlBase>
  );
}
